// TemplateSelector - Select a template to apply to a new workflow
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/api-client';
import {
  FileText,
  Star,
  CheckCircle,
  Calculator,
  CreditCard,
  Banknote,
  Building2,
  FolderOpen,
  Loader2,
  FileBarChart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TemplateConfig } from '@creditnova/shared';
import { useWorkflow } from '@/contexts';
import { applyTemplateToWorkflow } from '@/lib/template-utils';

interface Template {
  id: string;
  name: string;
  userId: string;
  description: string | null; 
  config: TemplateConfig;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface TemplateSelectorProps {
  onSelect?: (template: Template) => void;
  trigger?: React.ReactNode;
}

export const getTemplateFeatures = (config: any) => {
    const features = [];
    
    const modules = config.modules || {};
    const dataPoints = config.dataPoints || {};

    // Included if the new global object exists OR legacy summaryPreferences exists
    if (config.global || config.summaryPreferences) {
      features.push({ 
        icon: FileText, 
        label: 'Summary Preferences', 
        color: 'text-accent-500' 
      });
    }

    const hasFinancialExtraction = modules.financial?.enabled || (dataPoints.financialStatements?.length > 0);
  
    const metricsCount = modules.financial?.selectedMetricIds?.length || config.selectedMetrics?.length || dataPoints.metrics?.length || 0;

    if (hasFinancialExtraction || metricsCount > 0) {
      features.push({
        icon: FileBarChart, 
        label: metricsCount > 0 ? `Financials & Metrics` : 'Financial Extraction',
        color: 'text-blue-500',
      });
    }

    const customRatiosCount = modules.financial?.customRatios?.length || config.customRatios?.length || 0;
    if (customRatiosCount > 0) {
      features.push({
        icon: Calculator,
        label: `Custom Ratios (${customRatiosCount})`,
        color: 'text-cyan-500',
      });
    }

    const hasCreditReport = modules.creditReport?.enabled || !!config.summaryPreferences?.selectedDataPoints?.creditReport?.length;
    if (hasCreditReport) {
      const mode = modules.creditReport?.inclusionMode === 'automatic' ? ' (Auto)' : '';
      features.push({
        icon: CreditCard,
        label: `Credit Report Categories${mode}`,
        color: 'text-purple-500'
      });
    }

    const hasBank = modules.bank?.enabled || config.hasBankStatementAnalysis;
    if (hasBank) {
      features.push({ 
        icon: Banknote, 
        label: 'Bank Statement Config', 
        color: 'text-green-500' 
      });
    }

    const hasCorporate = modules.corporate?.enabled || !!config.summaryPreferences?.selectedDataPoints?.corporateSearch?.length;
    if (hasCorporate) {
      features.push({ 
        icon: Building2, 
        label: 'Corporate Search Config', 
        color: 'text-orange-500' 
      });
    }

    return features;
  };

export function TemplateSelector({ onSelect, trigger }: TemplateSelectorProps) {
  const { loadTemplate, goToStep, markStepComplete, updateData, data: workflowData } = useWorkflow();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const { data: templatesResponse, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
    enabled: open,
  });

  const templates = (templatesResponse?.data || []) as Template[];

  const handleApply = async () => {
    const template = templates.find((t) => t.id === selectedId);
    if (!template || isApplying) return;

    setIsApplying(true);

    try {
      console.log("template being used: ", template)
      // Compute the updates that loadTemplate will apply so we can read
      // summaryPreferences for the backend workflow creation call.
      // loadTemplate returns void (sets state internally), so we derive
      // the updates ourselves from the same utility it uses.
      const updates = applyTemplateToWorkflow(template.config, workflowData);
      loadTemplate(template);

      // Create a backend workflow + draft analysis so that workflowId is set
      // before navigating to the upload page (which guards on workflowId).
      const summaryPreferences = updates.summaryPreferences;
      const wf = await api.workflows.create({ summaryPreferences });
      const reportName = summaryPreferences?.reportName;
      await api.workflows.createAnalysis(wf.id, wf.analysisId, reportName);
      updateData({
        workflowId: wf.id,
        analysisId: wf.analysisId,
      });

      // Notify parent if an onSelect callback was provided
      onSelect?.(template);

      // Close the modal
      setOpen(false);

      // Navigate to the unified upload page (concurrent workflow).
      // The upload page reads selectedDataPoints from summaryPreferences
      // to determine which upload zones to render.
      goToStep('upload');
      // Mark summary as complete so the progress bar reflects the jump
      markStepComplete('summary');
    } catch (err) {
      console.error('Failed to create workflow from template:', err);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline">
            <FolderOpen className="w-4 h-4 mr-2" />
            Load Template
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Load Template</DialogTitle>
          <DialogDescription>
            Select a saved template to pre-configure your analysis workflow.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No templates available.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Complete an analysis and save it as a template to see it here.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-3">
              {templates.map((template) => {
                const features = getTemplateFeatures(template.config);
                const isSelected = selectedId === template.id;

                return (
                  <Card
                    key={template.id}
                    className={cn(
                      'p-4 cursor-pointer transition-all hover:border-accent-500/50',
                      isSelected && 'border-accent-500 bg-accent-500/5'
                    )}
                    onClick={() => setSelectedId(template.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5',
                          isSelected ? 'border-accent-500 bg-accent-500' : 'border-muted-foreground'
                        )}
                      >
                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold truncate">{template.name}</h4>
                          {template.isDefault && (
                            <Badge variant="secondary" className="gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Default
                            </Badge>
                          )}
                        </div>

                        {template.description && (
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                            {template.description}
                          </p>
                        )}

                        {features.length > 0 && (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 p-3 bg-muted/30 rounded-xl">
                            {features.map((feature, idx) => {
                              const Icon = feature.icon;
                              return (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                  <Icon className={cn('w-3.5 h-3.5 shrink-0', feature.color)} />
                                  <span className="font-medium truncate text-foreground/80">
                                    {feature.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!selectedId || isApplying}>
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              'Apply Template'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
